import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { AppDataContext } from '~/contexts/AppDataContext';
import { cva } from 'class-variance-authority';
import {
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { ArticleContext } from '~/contexts/ArticleContext';
import Image from '~/components/Shared/Image/Image';
import { useIsPremiumTemplate } from '~/utilities/useIsPremiumTemplate';
import { useVideoPlayerAsCover } from '~/utilities/experiments/videoPlacement';
import { useIsKyivTemplate } from '~/utilities/useIsKyivTemplate';

const cssClasses = cva('ml-1 pb-2 mt-4 md:m-3', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
    display: {
      true: '',
      false: 'hidden',
    },
  },
});

export default component$((): JSX.Element => {
  const {
    themeComponents: { image },
  } = useContext(AppDataContext);
  const isPremium = useIsPremiumTemplate();
  const isKyiv = useIsKyivTemplate();
  const article = useContext(ArticleContext);
  const { payloadToSSR } = useContext(AppDataContext);
  const { experiments, isMobileViewer } = payloadToSSR;

  if (!article || !article.cover) return <></>;

  let wrapperClass = '';

  let { aspectRatio, caption, credit, cropping } = article.cover.image.value;
  let smAspectRatio = aspectRatio;
  let mdAspectRatio = aspectRatio;
  const { alt, path, fileExtension, host } = article.cover.image.value;
  // default cover image sizes
  let imageWidths = { small: 360, medium: 720, large: 1080 };

  if (isPremium) {
    wrapperClass = 'flex-1';
    imageWidths = { small: 360, medium: 720, large: 800 };
    cropping = {
      height: cropping?.height as number,
      width: cropping?.height as number,
      x: cropping?.x || 0,
      y: cropping?.y || 0,
    };
    aspectRatio = '1/1';
    smAspectRatio = '1/1';
    mdAspectRatio = '1/1';
    caption = '';
    credit = '';
  }

  if (isKyiv) {
    wrapperClass = '';
    imageWidths = { small: 600, medium: 880, large: 1440 };
    cropping = {
      height: cropping?.height as number,
      width: cropping?.width as number,
      x: cropping?.x || 0,
      y: cropping?.y || 0,
    };
    smAspectRatio = '5/4';
    mdAspectRatio = '16/9';
    aspectRatio = '16/9';
    caption = '';
    credit = '';
  }

  // TODO: Remove once the video placement experiment ends.
  const hasVideoPlayerAsCover = useVideoPlayerAsCover(
    article,
    isMobileViewer ?? false,
    experiments
  );
  const noVideo = article.metadata?.commercialTags?.includes('no-video-player');

  if (hasVideoPlayerAsCover && !noVideo) return <></>;

  return (
    <Image
      alt={alt}
      aspectRatio={aspectRatio}
      smAspectRatio={smAspectRatio}
      mdAspectRatio={mdAspectRatio}
      path={path}
      caption={caption}
      credit={credit}
      cropping={cropping}
      host={host}
      fileExtension={fileExtension}
      wrapperClass={wrapperClass}
      captionClass={cssClasses({
        color: image.textColor.value,
        ...getFontSizeObject(image, 'fontSize'),
        display: !hasVideoPlayerAsCover,
      })}
      imageWidths={imageWidths}
      loading='eager'
    />
  );
});
